import React, { useState, useEffect} from 'react';
import '../view-archer-styles.css';

//material UI
import { Avatar, Divider, Dialog, DialogContent, DialogTitle, FormControl, Grid, IconButton, MenuItem, InputLabel, InputAdornment, Select, Slide, Stack, TextField, Typography } from '@mui/material/';
import { TransitionProps } from '@mui/material/transitions';
import { blue, red } from '@mui/material/colors';
import { BugReport, Close, Fullscreen, CloseFullscreen, Search} from '@mui/icons-material/';

//components import
import FlaggedItem from '../dataObjects/FlaggedItem';
import FlaggedItemsTable from './FlaggedItemsTable';
import ReportItem from '../dataObjects/ReportItem';
import ReportItemsTable from './ReportItemsTable';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';

//const data
import AuditPeriods from '../../../data/AuditPeriods';
import RecordTypeCategories from '../data/RecordTypeCategories';

interface FlaggedItemsEditorProps {
    filerCpfId: number;
    issueId: number;
    flaggedItems?: FlaggedItem[];
    isFlaggedItemsEditorOpen: boolean,
    closeFlaggedItemsEditor: () => void
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const FlaggedItemsEditor = (props: FlaggedItemsEditorProps) => {

    let taggedItems: FlaggedItem[] = new Array();
    if(props.flaggedItems)
        taggedItems = props.flaggedItems;
    const [flaggedItems, setFlaggedItems] = useState(taggedItems);
    const [isFullScreen, setFullScreen] = useState(false);

    //filter
    let defaultRecordTypeCategory = 'A';
    let defaultReportYear = new Date().getFullYear();
    let defaultReportMonth = new Date().getMonth();

    const dropDownYears = Array.from(new Array(5), (val, index) => defaultReportYear - index);

    const [recordTypeCategory, setRecordTypeCategory] = useState(defaultRecordTypeCategory);
    const [reportYear, setReportYear] = useState(defaultReportYear);
    const [reportMonth, setReportMonth] = useState(defaultReportMonth);
    const [searchName, setSearchInput] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');

    //reportItems
    let items: ReportItem[] = new Array();
    const [reportItems, setReportItems] = useState(items);

    const closeFlaggedItemsEditor = () => {
        props.closeFlaggedItemsEditor();
    }

    //initial payload
    useEffect(() => {
        getReportItems()
    }, [recordTypeCategory, reportYear, reportMonth, searchName, sortField, sortDirection]);

    const openFullScreen = () => {
        setFullScreen(true);
    }

    const closeFullScreen = () => {
        setFullScreen(false);
    }

    const getReportItems = async () => {
        var params = [
            `cpfId=${props.filerCpfId}`,
            `schedule=${recordTypeCategory}`,
            `year=${reportYear}`,
            `month=${reportMonth}`,
            `name=${searchName}`,
            `sortField=${sortField}`,
            `sortDirection=${sortDirection}`
        ].join('&');

        let url = `/Archer3/GetPotentialFlaggedItems?${params}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetPotentialFlaggedItems',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let data = result.result;
                    setReportItems(data);
                }
            })
    }

    const updateSearchName = (event: any) => {
        setSearchInput(event.target.value);
    }

    const updateRecordTypeCategory = (event: any) => {
        setRecordTypeCategory(event.target.value);
    }

    const updateReportYear = (event: any) => {
        setReportYear(event.target.value);
    }

    const updateReportMonth = (event: any) => {
        setReportMonth(event.target.value);
    }


    const updateSorting = (field: string, direction: string) => {
        setSortField(field);
        setSortDirection(direction);
    }

    //flaggedItem
    const removeFlaggedItem = (flaggedItemId: number) => {
        var params = [
            `flaggedItemId=${flaggedItemId}`,
        ].join('&');
        let url = `/Archer3/removeFlaggedItem?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RemoveFlaggedItem',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    //maybe just remove it from UI?
                    let items = flaggedItems.filter((item) => item.flaggedItemId != flaggedItemId);
                    setFlaggedItems(items);
                }
            })
    }

    const addFlaggedItem = (item: FlaggedItem) => {
        setFlaggedItems([...flaggedItems, item]);
    }

    const updateFlaggedItems = () => {

    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isFlaggedItemsEditorOpen}
                fullScreen={isFullScreen}
                scroll={'paper'}
                fullWidth={true}
                maxWidth={'md'}
                TransitionComponent={Transition}
                keepMounted
                onClose={closeFlaggedItemsEditor}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: red[500] }}><BugReport /></Avatar>
                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                            Flagged Items...
                        </Typography>
                    </Stack>
                </DialogTitle>
                <Stack direction="row" spacing={1}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    {   !isFullScreen && 
                        <IconButton aria-label="fullscreen" onClick={openFullScreen}>
                            <Fullscreen />
                        </IconButton>
                    }
                    { isFullScreen &&
                        <IconButton aria-label="fullscreen" onClick={closeFullScreen}>
                            <CloseFullscreen  />
                        </IconButton>
                    }
                    <IconButton aria-label="delete" onClick={closeFlaggedItemsEditor}>
                        <Close />
                    </IconButton>
                </Stack>
                <DialogContent dividers>
                    <Grid container>
                        <Stack direction="row" spacing={1}>
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                <InputLabel>Report Year</InputLabel>
                                <Select
                                    defaultValue={defaultReportYear}
                                    label="Reporting Year"
                                    onChange={(event) => updateReportYear(event)}
                                >
                                    {
                                        dropDownYears.map((year) =>
                                            <MenuItem value={year} key={year} >{year}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                <InputLabel>Period</InputLabel>
                                <Select
                                    defaultValue={defaultReportMonth}
                                    label="Period"
                                    onChange={(event) => updateReportMonth(event)}
                                >
                                    {
                                        AuditPeriods.map((period) =>
                                            <MenuItem key={period.id} value={period.id}>{period.description}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <FormControl sx={{ m: 1, minWidth: 180 }} size="small">
                                <InputLabel>Record Item Type</InputLabel>
                                <Select
                                    defaultValue={defaultRecordTypeCategory}
                                    label="Record Item Type"
                                    onChange={(event) => updateRecordTypeCategory(event)}
                                >
                                    {
                                        RecordTypeCategories.map((recordType) =>
                                            <MenuItem key={recordType.value} value={recordType.value}>{recordType.description}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                placeholder="Search Item..."
                                onChange={(event) => updateSearchName(event)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>
                    </Grid>
                    <Grid container sx={{ marginTop: 2 }}>
                        <Grid item xs={12} alignItems="left">
                            <ReportItemsTable issueId={props.issueId} recordItemType={recordTypeCategory} reportItems={reportItems} addFlaggedItem={addFlaggedItem} updateSorting={updateSorting}></ReportItemsTable>
                        </Grid>
                    </Grid>
                    <Divider light sx={{ marginTop: 2 }} />
                    <Grid container sx={{ marginTop: 2 }}>
                        <Grid item alignItems="left">
                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                                Saved flagged items
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container sx={{ marginTop: 2 }}>
                        <Grid item xs={12} alignItems="left">
                            <FlaggedItemsTable flagItems={flaggedItems} removeFlaggedItem={removeFlaggedItem} updateFlaggedItems={updateFlaggedItems}></FlaggedItemsTable>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )

}

export default FlaggedItemsEditor